import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Format } from 'services/Dates'
import { useUpdateSettings, useDateFilterOptions } from './hooks'
import { FilterDropDown, ClearButton } from '.'
import DateTimePicker from '../DateTimePicker'
import { MFilters } from 'types/filterTypes'
import getRangeForButton from './helpers/getRangeForButton'

import { t } from 'translations'
import { Dropdown } from 'ui'

type DateFilterButtonProps = {
  contentName: MFilters.ContentTypes
  withTime?: boolean
  isSingleDate?: boolean
  hasPayPeriodsFilters?: boolean
}

const DateFilterButton: FC<DateFilterButtonProps> = ({ contentName, withTime, isSingleDate, hasPayPeriodsFilters }) => {
  const filterKey: MFilters.FilterKey = `filter-${contentName}`
  const settings = useSelector((state: MStore.RootState) => state.settings)
  const savedOptions = useMemo(() => {
    return settings[filterKey] || {}
  }, [settings, filterKey])

  const rangeStart = 'rangeStart' in savedOptions ? savedOptions.rangeStart : undefined
  const rangeEnd = 'rangeEnd' in savedOptions ? savedOptions.rangeEnd : undefined

  const { saveOptions } = useUpdateSettings(contentName)
  const { getDateRange, dateOption, setDateOption, datesOptions } = useDateFilterOptions(contentName, withTime, hasPayPeriodsFilters)

  const onDateFilterChange = (dates: string[] | string) => {
    saveOptions(
      {
        ...savedOptions,
        rangeStart: Format(dates[0], { outFormat: withTime ? 'server' : 'date' }),
        rangeEnd: Format(dates.length === 2 ? dates[1] : dates[0], { outFormat: withTime ? 'server' : 'date' })
      },
      {}
    )
  }

  const datePickerOptions = {
    inline: true,
    ...(!isSingleDate ? { mode: 'range' } : {}),
    dateFormat: withTime ? 'Y-m-dTH:i' : 'Y-m-d',
    enableTime: withTime
  }

  return (
    <FilterDropDown
      buttonContent={
        <div className="d-flex flex-row justify-content-between align-items-baseline ">
          <div className="d-flex flex-row align-items-baseline">
            <i className={`fas fa-calendar-${rangeStart ? 'check text-primary' : 'alt'} mr-1 `} />
            <span className="d-md-flex d-none">{getRangeForButton(rangeStart, rangeEnd)}</span>
          </div>
          <i className={`fa fa-caret-down ml-2`} />
        </div>
      }
      splitButton={
        rangeStart ? (
          <ClearButton
            contentName={contentName}
            onClick={() => {
              setDateOption('')
              saveOptions({ ...savedOptions, rangeStart: undefined, rangeEnd: undefined })
            }}
          />
        ) : null
      }
      contentName={contentName}
      rangeStart={rangeStart}
      dropDownType="DateFilterButton"
      withTime={withTime}
    >
      {({ closeDropdown }) => (
        <div className="d-flex flex-column my-2" style={{ width: '350px' }}>
          {!isSingleDate && (
            <>
              {datesOptions.map((option, index) => (
                <Dropdown.Item
                  key={index}
                  className={`${dateOption === option ? 'active' : ''}`}
                  onClick={() => {
                    saveOptions({ ...savedOptions, ...getDateRange(option) })
                    closeDropdown()
                  }}
                >
                  {t(`searchBar.${option}`)}
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
            </>
          )}

          <div className="mx-auto">
            <DateTimePicker
              onChange={(values: string[] | string) => {
                const canSaveDates = (values.length === 2 && !isSingleDate) || (values.length === 1 && isSingleDate)
                if (canSaveDates) {
                  onDateFilterChange(values)
                  closeDropdown()
                }
              }}
              options={datePickerOptions}
              value={rangeStart && rangeEnd ? [rangeStart, rangeEnd] : null}
            />
          </div>
        </div>
      )}
    </FilterDropDown>
  )
}

export default DateFilterButton
